import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

/**
 * Admin modal for editing a single user's mutable attributes. Today the
 * primary use-case is toggling `pricing_tier` between StickerYou and
 * Evergreen, but also exposes company/name for quick fixes.
 */
export const UserEditModal = ({ open, user, onSave, onClose, saving }) => {
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company: '',
    pricing_tier: 'stickeryou',
    am_name: '',
    am_email: '',
    am_phone: '',
    am_title: 'Account Manager',
  });

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      company: user.company || '',
      pricing_tier: user.pricing_tier || 'stickeryou',
      am_name: user.account_manager?.name || '',
      am_email: user.account_manager?.email || '',
      am_phone: user.account_manager?.phone || '',
      am_title: user.account_manager?.title || 'Account Manager',
    });
  }, [user]);

  if (!open || !user) return null;

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // Assemble account_manager object only if name+email are provided.
    const { am_name, am_email, am_phone, am_title, ...core } = form;
    const payload = { ...core };
    if (am_name.trim() && am_email.trim()) {
      payload.account_manager = {
        name: am_name.trim(),
        email: am_email.trim(),
        phone: am_phone.trim() || null,
        title: am_title.trim() || 'Account Manager',
      };
    } else {
      payload.account_manager = null;
    }
    onSave(user.id, payload);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      data-testid="admin-user-edit-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#2F5D3A]">Edit User</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            data-testid="admin-user-edit-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={user.email}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded bg-gray-50 text-gray-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={handleChange('first_name')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
                data-testid="admin-user-edit-first-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={handleChange('last_name')}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
                data-testid="admin-user-edit-last-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input
              type="text"
              value={form.company}
              onChange={handleChange('company')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
              data-testid="admin-user-edit-company"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#2F5D3A] mb-1">
              Pricing Tier
            </label>
            <select
              value={form.pricing_tier}
              onChange={handleChange('pricing_tier')}
              className="w-full px-3 py-2 border-2 border-[#6FBF73] rounded focus:ring-2 focus:ring-[#4A7C59] bg-green-50 font-medium"
              data-testid="admin-user-edit-pricing-tier"
            >
              <option value="stickeryou">Standard Pricing (CAD) — Full Price</option>
              <option value="evergreen">Evergreen Pricing (CAD) — 30% OFF</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {form.pricing_tier === 'evergreen'
                ? 'Strategic Partner — receives 30% off standard pricing.'
                : 'Public retail pricing (self sign-up default).'}
            </p>
          </div>

          {/* Account Manager assignment */}
          <div className="border-t pt-4">
            <label className="block text-sm font-semibold text-[#2F5D3A] mb-2">
              Account Manager
              <span className="text-xs font-normal text-gray-500 ml-2">
                (Optional — shown to the user in TopNav + Dashboard)
              </span>
            </label>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={form.am_name}
                  onChange={handleChange('am_name')}
                  placeholder="Rep name"
                  className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
                  data-testid="admin-user-edit-am-name"
                />
                <input
                  type="email"
                  value={form.am_email}
                  onChange={handleChange('am_email')}
                  placeholder="rep@eg3.ca"
                  className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
                  data-testid="admin-user-edit-am-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="tel"
                  value={form.am_phone}
                  onChange={handleChange('am_phone')}
                  placeholder="Phone (optional)"
                  className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
                  data-testid="admin-user-edit-am-phone"
                />
                <input
                  type="text"
                  value={form.am_title}
                  onChange={handleChange('am_title')}
                  placeholder="Title"
                  className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#6FBF73]"
                  data-testid="admin-user-edit-am-title"
                />
              </div>
              <p className="text-xs text-gray-500">
                Leave Rep name + email empty to remove an existing assignment.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              data-testid="admin-user-edit-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] text-white rounded font-medium hover:shadow-md disabled:opacity-60"
              data-testid="admin-user-edit-save"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
